/**
 * Widget registry — the plugin seam.
 *
 * A widget is a (type, schema, component) triple. Core validates configs
 * against the schemas registered here without ever importing a component, and
 * the renderer looks up components without ever importing a schema.
 *
 * Today everything is registered at startup by `registerBuiltins()`. When a
 * plugin system arrives, third-party widgets call `registerWidget` too and
 * nothing else in the system has to change — which is the whole point of
 * paying for this indirection now.
 */

import type { ComponentType } from 'preact';
import type { ZodType, ZodTypeDef } from 'zod';
import type { WidgetSchemaLookup } from '@hud/core';

export interface WidgetProps<C> {
  config: C;
}

export interface WidgetDef<C = unknown> {
  type: string;
  /**
   * Input is `unknown`, not `C`: these schemas apply defaults and coercions, so
   * what a config file may contain is deliberately looser than what a widget
   * receives. Typing the input as `C` would reject every schema with a default.
   */
  schema: ZodType<C, ZodTypeDef, unknown>;
  component: ComponentType<WidgetProps<C>>;
}

const registry = new Map<string, WidgetDef<never>>();

export function registerWidget<C>(def: WidgetDef<C>): void {
  if (registry.has(def.type)) {
    throw new Error(`Widget type "${def.type}" is already registered`);
  }
  registry.set(def.type, def as unknown as WidgetDef<never>);
}

export function getWidget(type: string): WidgetDef<never> | undefined {
  return registry.get(type);
}

export function registeredTypes(): string[] {
  return [...registry.keys()].sort();
}

/** The view of the registry that `core`'s engine consumes. Schemas only. */
export const widgetSchemas: WidgetSchemaLookup = {
  get: (type) => registry.get(type)?.schema,
  types: () => registeredTypes(),
};
